import React, { useEffect } from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import {
  WithStyles,
  Paper,
  Typography,
  Button,
  InputLabel
} from "@material-ui/core";
import { TextField } from "formik-material-ui";
import {
  FormikSubmitDispatchProps,
  ProjectForm,
  Project,
  SelectFieldOption,
  TabComponentProps
} from "../../../redux/types";
import { Link, LinkProps } from "react-router-dom";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import { AutoCompleteSelect } from "../../AutoCompleteSelect";
import { useTranslate } from "../../../services/appLanguageService";
import store from "../../../redux/store";
import { setTitle } from "../../../redux/reducers/tabRouter/tabTitle/actions";

let ValidationSchema = (t: any) =>
  Yup.object().shape({
    name: Yup.string()
      .min(1, t("minLengthError"))
      .max(255, t("max255LengthError"))
      .required(t("requiredError")),
    invoice_reference: Yup.string()
      .min(1, t("minLengthError"))
      .max(255, t("max255LengthError"))
      .nullable()
      .notRequired(),
    notes: Yup.string().nullable().notRequired(),
    businessarea: Yup.number()
      .positive(t("requiredError"))
      .required(t("requiredError"))
  });

const styles = (theme: any) =>
  createStyles({
    form: {
      width: "100%", // Fix IE 11 issue.
      marginTop: theme.spacing(1)
    },
    submit: {
      marginTop: theme.spacing(3)
    },
    paper: {
      padding: 20,
      marginTop: 20
    },
    selectLabel: {
      marginTop: theme.spacing(3)
    },
    select: {
      marginTop: theme.spacing(1)
    }
  });

interface NewProjectProps
  extends FormikSubmitDispatchProps<ProjectForm>,
    WithStyles<typeof styles>,
    TabComponentProps {
  project?: Project;
  businessAreaOptions: SelectFieldOption[];
  loadBusinessAreas: (searchTerm?: string) => void;
}

const NewEditProject: React.FC<NewProjectProps> = (props) => {
  const { classes, project, businessAreaOptions, loadBusinessAreas, tabId } =
    props;
  const t = useTranslate("ProjectPage");
  const t3 = useTranslate("ValidationErrorMessages");
  let initialValues: Project | ProjectForm;
  if (project && (project as Project).id) {
    initialValues = {
      ...project
    };
  } else {
    initialValues = {
      id: -1,
      name: "",
      invoice_reference: "",
      notes: "",
      businessarea: -1
    };
  }
  useEffect(() => {
    if (project) {
      store.dispatch(setTitle(project.name, tabId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        component={React.forwardRef<HTMLAnchorElement, Partial<LinkProps>>(
          (props, ref) => (
            <Link
              to={`/contracts/projects${
                project ? `/${project.id}` : ""
              }?same_tab=true`}
              {...props}
              ref={ref as any}
            />
          )
        )}
      >
        <NavigateBeforeIcon /> {t("navigateBeforeIconLabel")}
      </Button>
      <Paper className={classes.paper}>
        <Typography component="h1" variant="h6">
          {project ? t("editProjectHeading") : t("newProjectHeading")}
        </Typography>
        <Formik
          initialValues={initialValues}
          validationSchema={ValidationSchema(t3)}
          onSubmit={props.handleSubmit}
        >
          {(props) => (
            <form
              className={classes.form}
              onSubmit={props.handleSubmit}
              autoComplete="off"
            >
              <Field
                id="project-form-name"
                type="text"
                name="name"
                label={t("nameLabel")}
                placeholder={t("namePlaceholderLabel")}
                component={TextField}
                margin="normal"
                fullWidth
              />

              <InputLabel
                className={classes.selectLabel}
                htmlFor="form-businessarea"
              >
                {t("businessAreaLabel")}
              </InputLabel>
              <Field
                id="form-businessarea"
                type="text"
                name="businessarea"
                label={t("businessAreaLabel")}
                placeholder={t("businessAreaPlaceholder")}
                value={
                  businessAreaOptions.find(
                    (businessArea) =>
                      props.values.businessarea === businessArea.value
                  )
                    ? businessAreaOptions.find(
                        (businessArea) =>
                          props.values.businessarea === businessArea.value
                      )
                    : null
                }
                options={businessAreaOptions}
                component={AutoCompleteSelect}
                margin="normal"
                fullWidth
                loadOptions={loadBusinessAreas}
              />

              <Field
                id="new-project-form-invoice_reference"
                type="text"
                name="invoice_reference"
                label={t("invoiceReferenceLabel")}
                placeholder={t("invoiceReferencePlaceholderLabel")}
                component={TextField}
                multiline={true}
                margin="normal"
                rows="4"
                fullWidth
              />

              <Field
                id="new-project-form-notes"
                type="text"
                name="notes"
                label={t("notesLabel")}
                placeholder={t("notesPlaceholderLabel")}
                component={TextField}
                multiline={true}
                margin="normal"
                rows="4"
                fullWidth
              />

              <Button
                id="new-project-form-submit"
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {project ? t("saveButtonLabel") : t("createButtonLabel")}
              </Button>
            </form>
          )}
        </Formik>
      </Paper>
    </div>
  );
};

export default withStyles(styles)(NewEditProject);
